import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAnuncios } from '../hooks/useAnuncios';
import { AnuncioCardCompact } from '../components/anuncios/AnuncioGrid';
import { PageLoading, EmptyState } from '../components/common/Loading';
import { ConfirmModal } from '../components/common/Modal';
import Button from '../components/common/Button';
import { Anuncio } from '../types';

const MeusAnuncios: React.FC = () => {
  const {
    meusAnuncios,
    isLoading,
    carregarMeusAnuncios,
    pausarAnuncio,
    ativarAnuncio,
    deletarAnuncio,
    marcarVendido,
  } = useAnuncios();

  const [filtroStatus, setFiltroStatus] = useState<string>('todos');
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; anuncio?: Anuncio }>({
    open: false,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    carregarMeusAnuncios();
  }, []);

  const handleDelete = async () => {
    if (!deleteModal.anuncio) return;
    setIsDeleting(true);
    await deletarAnuncio(deleteModal.anuncio.id);
    setIsDeleting(false);
    setDeleteModal({ open: false });
  };

  const filteredAnuncios = meusAnuncios.filter((anuncio) => {
    if (filtroStatus === 'todos') return true;
    return anuncio.status === filtroStatus;
  });

  const statusCounts = {
    todos: meusAnuncios.length,
    ativo: meusAnuncios.filter((a) => a.status === 'ativo').length,
    pausado: meusAnuncios.filter((a) => a.status === 'pausado').length,
    vendido: meusAnuncios.filter((a) => a.status === 'vendido').length,
    pendente: meusAnuncios.filter((a) => a.status === 'pendente').length,
  };

  if (isLoading) {
    return <PageLoading message="Carregando seus anúncios..." />;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container-custom">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              Meus Anúncios
            </h1>
            <p className="text-gray-600 mt-1">
              Gerencie seus anúncios publicados
            </p>
          </div>
          <Button
            as="link"
            href="/criar-anuncio"
            leftIcon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            }
          >
            Novo Anúncio
          </Button>
        </div>

        {/* Status Filter Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { value: 'todos', label: 'Todos' },
            { value: 'ativo', label: 'Ativos' },
            { value: 'pausado', label: 'Pausados' },
            { value: 'vendido', label: 'Vendidos' },
            { value: 'pendente', label: 'Pendentes' },
          ].map((tab) => (
            <button
              key={tab.value}
              onClick={() => setFiltroStatus(tab.value)}
              className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                filtroStatus === tab.value
                  ? 'bg-green-700 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              {tab.label}
              <span className="ml-1.5 px-2 py-0.5 bg-white/20 rounded-full text-sm">
                {statusCounts[tab.value as keyof typeof statusCounts]}
              </span>
            </button>
          ))}
        </div>

        {/* Anuncios List */}
        {meusAnuncios.length === 0 ? (
          <EmptyState
            icon="📋"
            title="Você ainda não tem anúncios"
            description="Comece a vender criando seu primeiro anúncio"
            action={
              <Button as="link" href="/criar-anuncio">
                Criar Primeiro Anúncio
              </Button>
            }
          />
        ) : filteredAnuncios.length === 0 ? (
          <EmptyState
            icon="🔍"
            title="Nenhum anúncio encontrado"
            description={`Você não tem anúncios com status "${filtroStatus}"`}
            action={
              <Button variant="outline" onClick={() => setFiltroStatus('todos')}>
                Ver todos
              </Button>
            }
          />
        ) : (
          <div className="grid gap-4">
            {filteredAnuncios.map((anuncio) => (
              <AnuncioCardCompact
                key={anuncio.id}
                anuncio={anuncio}
                onEdit={() => {
                  // Navigate to edit page
                  window.location.href = `/editar-anuncio/${anuncio.id}`;
                }}
                onPause={() => pausarAnuncio(anuncio.id)}
                onActivate={() => ativarAnuncio(anuncio.id)}
                onDelete={() => setDeleteModal({ open: true, anuncio })}
                onMarkSold={() => marcarVendido(anuncio.id)}
              />
            ))}
          </div>
        )}

        {/* Stats Summary */}
        {meusAnuncios.length > 0 && (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-gray-900">
                {meusAnuncios.reduce((sum, a) => sum + a.visualizacoes, 0).toLocaleString('pt-BR')}
              </p>
              <p className="text-sm text-gray-500">Visualizações totais</p>
            </div>
            <div className="bg-white rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-gray-900">
                {meusAnuncios.reduce((sum, a) => sum + a.favoritosCount, 0).toLocaleString('pt-BR')}
              </p>
              <p className="text-sm text-gray-500">Favoritos</p>
            </div>
            <div className="bg-white rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-green-700">
                {statusCounts.ativo}
              </p>
              <p className="text-sm text-gray-500">Anúncios ativos</p>
            </div>
            <div className="bg-white rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-blue-700">
                {statusCounts.vendido}
              </p>
              <p className="text-sm text-gray-500">Vendidos</p>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false })}
        onConfirm={handleDelete}
        title="Excluir Anúncio"
        message={`Tem certeza que deseja excluir "${deleteModal.anuncio?.titulo}"? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        variant="danger"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default MeusAnuncios;
